/**
 * TodayDashboardPanel
 *
 * Admin → "Today" tab. Single-glance view of what the team has done so
 * far today: quotations created, walk-ins logged, follow-ups due
 * tomorrow, per-salesperson leaderboard. Auto-refreshes every 60s so
 * the admin doesn't have to reload the page to see live activity.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  RefreshCw, FileText, Users, MessageCircle, AlertTriangle,
  CalendarClock, TrendingUp, Trophy, ChevronLeft, ChevronRight, CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { todayIst } from '../../lib/store';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (n) => {
  const num = Number(n || 0);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${Math.round(num)}`;
};

function DeltaChip({ today, yesterday, valueFormatter, suffix = '', compareLabel = 'yesterday' }) {
  // Compare current period's number to the same window in the comparison period.
  // Renders a coloured chip showing absolute and % delta.
  const t = Number(today || 0);
  const y = Number(yesterday || 0);
  const diff = t - y;
  if (y === 0 && t === 0) return null;
  const shortLabel = compareLabel === 'yesterday' ? 'yest.' : 'prev. day';
  if (diff === 0) {
    return (
      <span className="inline-flex items-center text-[9px] sm:text-[10px] text-[#5C6150] bg-[#F2F4EF] rounded-full px-1.5 py-0.5 mt-1">
        = vs {shortLabel}
      </span>
    );
  }
  const pct = y === 0 ? null : Math.round((diff / Math.max(1, y)) * 100);
  const isUp = diff > 0;
  const colour = isUp ? 'text-[#1B7531] bg-[#25D366]/10' : 'text-[#C62828] bg-red-50';
  const arrow = isUp ? '▲' : '▼';
  const absDisp = valueFormatter ? valueFormatter(Math.abs(diff)) : Math.abs(diff);
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] sm:text-[10px] rounded-full px-1.5 py-0.5 mt-1 ${colour}`}>
      {arrow} {absDisp}{suffix}{pct !== null ? ` (${isUp ? '+' : ''}${pct}%)` : ''} vs {shortLabel}
    </span>
  );
}

function StatTile({ icon, label, value, hint, accent, testId, delta }) {
  const Icon = icon;
  return (
    <div
      className={`rounded-xl border bg-white p-4 sm:p-5 transition-shadow hover:shadow-md ${accent || 'border-[#E2E6D9]'}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] sm:text-xs uppercase tracking-wider text-[#5C6150]">{label}</span>
        <Icon className="w-4 h-4 text-[#556B2F]" />
      </div>
      <div className="text-2xl sm:text-3xl font-bold text-[#1A1C16] font-mono-data">{value}</div>
      {hint && <p className="text-[10px] sm:text-xs text-[#8F9485] mt-1">{hint}</p>}
      {delta}
    </div>
  );
}

// Compute today's IST date as YYYY-MM-DD (used as the default selection
// and as the upper bound on the date picker). iter-119b: switched to
// the shared `todayIst` helper — the old inline math added
// `getTimezoneOffset() + 5.5h` which double-counted on IST devices and
// silently returned the UTC date instead of the IST one.
const getTodayISTDateString = todayIst;

function shiftDate(yyyyMmDd, deltaDays) {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  const ny = dt.getUTCFullYear();
  const nm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const nd = String(dt.getUTCDate()).padStart(2, '0');
  return `${ny}-${nm}-${nd}`;
}

function prettyDate(yyyyMmDd) {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-IN', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

export default function TodayDashboardPanel() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const todayStr = useMemo(() => getTodayISTDateString(), []);
  // Seed the picker from `?date=YYYY-MM-DD` (used by the
  // QuotationDetail Back button so the user lands on the same day they
  // clicked through from). Falls back to today when absent or malformed.
  const initialDate = useMemo(() => {
    const raw = (searchParams.get('date') || '').trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(raw) && raw <= todayStr ? raw : todayStr;
  }, [searchParams, todayStr]);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const isToday = selectedDate === todayStr;

  const fetchStats = useCallback(async (dateOverride) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('heda_token');
      const d = dateOverride || selectedDate;
      const res = await axios.get(`${API_BASE}/admin/today-stats`, {
        params: { date: d },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(res.data);
      setLastFetch(new Date());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Initial load whenever the selected date changes.
  // We schedule via queueMicrotask so the state-setter calls inside
  // fetchStats happen *after* the effect returns — required to satisfy
  // the new `react-hooks/set-state-in-effect` rule.
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => { if (!cancelled) fetchStats(); });
    return () => { cancelled = true; };
  }, [fetchStats]);

  // Auto-refresh every 60s ONLY when viewing today. For past days the
  // numbers are frozen so polling is pointless.
  useEffect(() => {
    if (!isToday) {
      return undefined;
    }
    const id = setInterval(() => { fetchStats(); }, 60000);
    return () => clearInterval(id);
  }, [fetchStats, isToday]);

  if (!data && loading) {
    return <p className="text-sm text-[#8F9485] py-12 text-center">Loading stats…</p>;
  }
  if (!data) {
    return <p className="text-sm text-[#8F9485] py-12 text-center">No data yet.</p>;
  }

  const q = data.quotations;
  const w = data.walkins;
  const y = q.yesterday || { total_count: 0, total_value: 0, by_status: { closed: 0, pending: 0, lost: 0 } };
  const closedRatio = q.total_count > 0 ? Math.round((q.by_status.closed / q.total_count) * 100) : 0;
  const compareLabel = data.compare_label || (isToday ? 'yesterday' : 'previous day');

  return (
    <div className="space-y-5" data-testid="admin-today-panel">
      {/* Header + Date Navigator */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-bold text-[#1A1C16] flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#556B2F]" />
            {isToday ? "Today's Live Dashboard" : `Dashboard — ${prettyDate(selectedDate)}`}
          </h2>
          <p className="text-[11px] text-[#8F9485] mt-0.5">
            <span className="font-semibold text-[#1A1C16]">{data.date_ist}</span>
            {' · '}Last updated: {lastFetch ? lastFetch.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
            {isToday ? ' · Auto-refresh every 60s' : ' · Historical view (no auto-refresh)'}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap" data-testid="today-date-controls">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedDate((d) => shiftDate(d, -1))}
            disabled={loading}
            data-testid="today-prev-day-btn"
            title="Previous day"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="relative">
            <CalendarDays className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#556B2F] pointer-events-none" />
            <input
              type="date"
              value={selectedDate}
              max={todayStr}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                if (v > todayStr) {
                  toast.error('Cannot view a future date');
                  return;
                }
                setSelectedDate(v);
              }}
              className="h-8 pl-7 pr-2 text-xs border border-[#E2E6D9] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-[#556B2F]"
              data-testid="today-date-picker"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedDate((d) => (d === todayStr ? d : shiftDate(d, 1)))}
            disabled={loading || isToday}
            data-testid="today-next-day-btn"
            title="Next day"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          {!isToday && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDate(todayStr)}
              disabled={loading}
              data-testid="today-jump-today-btn"
              className="text-[#556B2F] hover:bg-[#556B2F]/10"
            >
              Jump to today
            </Button>
          )}
          <Button onClick={() => fetchStats()} variant="outline" size="sm" disabled={loading} data-testid="today-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI tiles — value-first Quotations, dropped the
          Closed/Pending/Lost tile (the new Quotations-created list +
          the Top Salespeople table already carry that info), grid
          collapsed from 4 → 3 columns. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <StatTile
          icon={FileText}
          label={isToday ? 'Quotations Today' : 'Quotations'}
          value={formatINR(q.total_value)}
          hint={q.total_count > 0
            ? `${q.total_count} quotation${q.total_count === 1 ? '' : 's'} · ${closedRatio}% closed`
            : 'No quotations'}
          accent="border-[#556B2F]/30"
          testId="today-tile-quotations"
          delta={<DeltaChip today={q.total_value} yesterday={y.total_value} valueFormatter={formatINR} compareLabel={compareLabel} />}
        />
        <StatTile
          icon={Users}
          label={isToday ? 'Walk-ins Logged' : 'Walk-ins'}
          value={w.today_count}
          hint={w.pending_review > 0 ? `⚠ ${w.pending_review} need review` : 'All clean'}
          accent={w.pending_review > 0 ? 'border-[#B07700]/40' : 'border-[#E2E6D9]'}
          testId="today-tile-walkins"
          delta={<DeltaChip today={w.today_count} yesterday={w.yesterday_count} compareLabel={compareLabel} />}
        />
        <StatTile
          icon={CalendarClock}
          label={isToday ? 'Follow-ups Due Tomorrow' : 'Follow-ups Due Next Day'}
          value={w.follow_ups_tomorrow}
          hint={w.follow_ups_tomorrow > 0 ? "Don't miss these" : 'Schedule is clear'}
          accent="border-[#556B2F]/30"
          testId="today-tile-followups"
        />
      </div>

      {/* Pending review banner */}
      {w.pending_review > 0 && (
        <div className="bg-[#FFF8E1] border border-[#E2D9A0] rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-[#B07700] mt-0.5 shrink-0" />
          <div className="text-xs text-[#5C4500]">
            <span className="font-semibold">{w.pending_review} walk-in{w.pending_review > 1 ? 's' : ''}</span>
            {' '}couldn&apos;t be auto-transcribed. Open the <span className="font-semibold">Walk-ins tab</span> to listen and complete them.
          </div>
        </div>
      )}

      {/* Quotations created on the selected day — placed where
          the old Closed/Pending/Lost tile used to sit. Newest first;
          respects the panel's date picker (past-day view lists that day's
          quotes). Click a row to open the quotation. */}
      <Card className="border-[#E2E6D9]" data-testid="today-quotations-list-card">
        <CardContent className="pt-4">
          <h3 className="text-sm font-semibold text-[#1A1C16] uppercase tracking-wide mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#556B2F]" />
            {isToday ? 'Quotations Created Today' : `Quotations Created — ${prettyDate(selectedDate)}`}
            <span className="text-[10px] text-[#8F9485] normal-case tracking-normal">
              ({(q.list || []).length})
            </span>
          </h3>
          {(q.list || []).length === 0 ? (
            <p className="text-xs text-[#8F9485] py-4 text-center italic" data-testid="today-quotations-list-empty">
              {isToday ? 'No quotations created yet today.' : 'No quotations were created on this day.'}
            </p>
          ) : (
            <div className="border border-[#E2E6D9] rounded-md overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-xs min-w-[560px]">
                <thead className="bg-[#F2F4EF] text-[#5C6150] sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3 whitespace-nowrap">Quote #</th>
                    <th className="text-left py-2 px-3">Customer</th>
                    <th className="text-left py-2 px-3">Salesperson</th>
                    <th className="text-right py-2 px-3">Value</th>
                    <th className="text-center py-2 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.list || []).map((row) => {
                    const st = (row.status || 'pending').toLowerCase();
                    const badge = st === 'closed'
                      ? { cls: 'bg-[#556B2F]/15 text-[#3D5320]', label: '✓ Closed' }
                      : st === 'lost'
                        ? { cls: 'bg-[#C44A4A]/15 text-[#8B2E2E]', label: '✕ Lost' }
                        : { cls: 'bg-[#DAA520]/15 text-[#8B6914]', label: '● Pending' };
                    return (
                      <tr
                        key={row.id}
                        className="border-t border-[#E2E6D9] hover:bg-[#F9FAF7] cursor-pointer"
                        onClick={() => navigate(`/quotations/${row.id}`)}
                        data-testid={`today-quotation-row-${row.id}`}
                      >
                        <td className="py-2 px-3 font-mono-data text-[#556B2F] whitespace-nowrap">
                          {row.quotation_number || '—'}
                        </td>
                        <td className="py-2 px-3 truncate max-w-[180px]" title={row.customer_name}>
                          {row.customer_name || '—'}
                        </td>
                        <td className="py-2 px-3">
                          <span>{row.created_by_name || '—'}</span>
                          {row.created_by_code && (
                            <Badge variant="outline" className="ml-1.5 text-[9px]">{row.created_by_code}</Badge>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right font-mono-data text-[#DAA520]">
                          {formatINR(row.grand_total)}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${badge.cls}`}>
                            {badge.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card className="border-[#E2E6D9]">
        <CardContent className="pt-4">
          <h3 className="text-sm font-semibold text-[#1A1C16] uppercase tracking-wide mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#DAA520]" /> {isToday ? 'Top Salespeople Today' : `Top Salespeople — ${prettyDate(selectedDate)}`}
          </h3>
          {q.top_salespeople.length === 0 ? (
            <p className="text-xs text-[#8F9485] py-4 text-center italic">
              {isToday
                ? 'No quotations yet today — leaderboard updates live.'
                : 'No quotations on this day.'}
            </p>
          ) : (
            <div className="border border-[#E2E6D9] rounded-md overflow-x-auto">
              <table className="w-full text-xs min-w-[480px]">
                <thead className="bg-[#F2F4EF] text-[#5C6150]">
                  <tr>
                    <th className="text-left py-2 px-3 w-8">#</th>
                    <th className="text-left py-2 px-3">Salesperson</th>
                    <th className="text-right py-2 px-3">Quotes</th>
                    <th className="text-right py-2 px-3">Value</th>
                    <th className="text-center py-2 px-3 whitespace-nowrap">Closed / Pending / Lost</th>
                  </tr>
                </thead>
                <tbody>
                  {q.top_salespeople.map((u, i) => (
                    <tr key={u.user_id} className="border-t border-[#E2E6D9]" data-testid={`today-leader-${u.user_id}`}>
                      <td className="py-2 px-3 font-mono-data text-[#8F9485]">{i + 1}</td>
                      <td className="py-2 px-3">
                        <span className="font-medium">{u.user_name}</span>
                        {u.staff_code && <Badge variant="outline" className="ml-2 text-[9px]">{u.staff_code}</Badge>}
                      </td>
                      <td className="py-2 px-3 text-right font-mono-data font-semibold">{u.count}</td>
                      <td className="py-2 px-3 text-right font-mono-data text-[#DAA520]">{formatINR(u.value)}</td>
                      <td className="py-2 px-3 text-center font-mono-data">{u.closed} / {u.pending} / {u.lost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[10px] text-[#8F9485] flex items-center gap-1 justify-center pt-2">
        <MessageCircle className="w-3 h-3" /> A full digest is sent to your configured WhatsApp number every evening at 9:00 PM IST.
      </p>
    </div>
  );
}
